import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "../_app/lib/query";
import { AjaxResponse } from "../_app/api";
import { deleteFilter, getAllFilters, getFilterValues, getUsersFilters, saveFilters } from "./api";
import { useCallback, useMemo } from "react";
import { useFeedbackAlerts, useQueryParams } from "../_app/hooks";
import { FilterValues, Query, UsersFilter } from "./types";
import { useContextProvider } from "../context/providers/ContextProvider";

export function useAllFilters(usage: string = "", options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["filters", { accountId, usage }],
    queryFn: () => getAllFilters(accountId, usage),
    ...options,
  });
}

export function useFilterValues(resource: string | undefined, search?: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useInfiniteQuery<FilterValues, AjaxResponse>({
    queryKey: ["filter", { resource, search, accountId }],
    queryFn: ({ pageParam }) => getFilterValues(accountId, resource, pageParam as number | undefined, search),
    initialPageParam: 0,
    getNextPageParam: () => undefined,
    ...options,
  });
}

export function useUserFilters(usage: string, options = {}) {
  const { contextId: accountId } = useContextProvider();
  return useQuery({
    queryKey: ["usersFilters", { accountId, usage }],
    queryFn: () => getUsersFilters(accountId, usage),
    ...options,
  });
}

export function useSaveFilters(usage: string, options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (filters: UsersFilter) => saveFilters(accountId, usage, filters),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["usersFilters", { accountId, usage }],
      });
      setFeedbackAlertSuccess("Filter saved.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to save filter.");
    },

    ...options,
  });
}

export function useDeleteFilter(usage: string, options = {}) {
  const queryClient = useQueryClient();
  const { setFeedbackAlertError, setFeedbackAlertSuccess } = useFeedbackAlerts();
  const { contextId: accountId } = useContextProvider();
  return useMutation({
    mutationFn: (filterId: any) => deleteFilter(accountId, usage, filterId),

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["usersFilters", { accountId, usage }],
      });
      setFeedbackAlertSuccess("Filter deleted.");
    },

    onError: (error: any) => {
      setFeedbackAlertError(error?.data?.message || "Failed to delete filter.");
    },

    ...options,
  });
}

const dateRangeFilter = [
  {
    id: "4",
    name: "Date Range",
    options: {},
    resources: null,
    showToggle: true,
    type: "DATE_RANGE",
    values: [],
  },
];

export function useStatementsFilters(options = {}) {
  return useQuery({
    queryKey: ["statements-filters"],
    queryFn: () => dateRangeFilter,
    ...options,
  });
}

export function useTriggeredAlertsFilters(options = {}) {
  return useQuery({
    queryKey: ["triggered-alerts-filters"],
    queryFn: () => dateRangeFilter,
    ...options,
  });
}

export function useBundleAlertsFilters(options = {}) {
  return useQuery({
    queryKey: ["bundle-alerts-filters"],

    queryFn: () => [
      {
        id: "62",
        name: "CLI/Identifier",
        type: "IDENTIFIER",
        resources: "/filters/ALERTS/values/clis",
        showToggle: true,
        values: [],
        options: { SEARCH_BAR: true, BULK_INPUT: true },
      },
    ],

    ...options,
  });
}

export function useFilterQueries() {
  const queryParams = useQueryParams();
  const getQueries = useCallback(() => {
    const filters: Query[] = [];
    queryParams.forEach((value, key) => {
      filters.push({
        id: key,
        value,
      });
    });
    return filters;
  }, [queryParams]);
  const queries = useMemo(() => getQueries(), [getQueries]);
  return queries;
}
